/**
 * The Edit menu's Undo, Redo, Cut, Copy, Paste and the rest, from a menu click.
 *
 * The desktop handed these to Electron, which acted as the operating system's
 * own Edit menu would. A page has no such thing, so each is carried out by
 * whichever editor has the document: ProseMirror's history and commands in the
 * formatted view, CodeMirror's in source view, the browser's own in a text
 * field such as the find bar. The keystrokes never come here: the editors
 * handle those themselves (see EDITOR_DELEGATED).
 */
import { undo, redo } from '@milkdown/kit/prose/history'
import { deleteSelection, selectAll } from '@milkdown/kit/prose/commands'
import { undo as cmUndo, redo as cmRedo, selectAll as cmSelectAll } from '@codemirror/commands'
import type { EditorView as CmView } from '@codemirror/view'
import type { EditorView as PmView } from '@milkdown/kit/prose/view'
import { activeDoc } from '../stores/documents'
import { shownSourceFor } from '../editor/source-registry'
import { withView } from '../editor/view'
import { notify } from '../stores/notifications'
import type { EditAction } from '../../../platform/api'

/** A text field outside the editors has the focus: the find bar, a name box. */
function focusedField(): HTMLInputElement | HTMLTextAreaElement | null {
  const el = document.activeElement
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el
  return null
}

function sourceView(): CmView | null {
  const doc = activeDoc.value
  if (!doc?.sourceMode) return null
  return shownSourceFor(doc.id)?.view ?? null
}

/**
 * Pastes text the clipboard holds, as the editor would have received it from
 * Ctrl+V: a paste event carrying the data, which ProseMirror and CodeMirror
 * both read. Reading the clipboard needs the browser's permission; Ctrl+V
 * needs none, and the message says so when the read is refused.
 */
async function paste(plain: boolean, target: HTMLElement): Promise<void> {
  const data = new DataTransfer()
  try {
    if (!plain && navigator.clipboard?.read) {
      for (const item of await navigator.clipboard.read()) {
        for (const type of item.types) {
          if (type === 'text/html' || type === 'text/plain') {
            data.setData(type, await (await item.getType(type)).text())
          }
        }
      }
    } else {
      data.setData('text/plain', await navigator.clipboard.readText())
    }
  } catch {
    notify('The browser did not let the page read the clipboard. Press Ctrl+V to paste.', {
      kind: 'error',
      key: 'paste',
    })
    return
  }
  if (!data.types.length) return
  target.focus()
  target.dispatchEvent(
    new ClipboardEvent('paste', { clipboardData: data, bubbles: true, cancelable: true })
  )
}

/** A text field takes no notice of a paste event it did not make: the text goes in directly. */
async function pasteIntoField(field: HTMLInputElement | HTMLTextAreaElement): Promise<void> {
  let text: string
  try {
    text = await navigator.clipboard.readText()
  } catch {
    notify('The browser did not let the page read the clipboard. Press Ctrl+V to paste.', {
      kind: 'error',
      key: 'paste',
    })
    return
  }
  field.focus()
  field.setRangeText(
    text,
    field.selectionStart ?? field.value.length,
    field.selectionEnd ?? field.value.length,
    'end'
  )
  field.dispatchEvent(new Event('input', { bubbles: true }))
}

function inProse(action: EditAction, view: PmView): void {
  const { state, dispatch } = view
  switch (action) {
    case 'undo':
      undo(state, dispatch)
      break
    case 'redo':
      redo(state, dispatch)
      break
    case 'selectAll':
      selectAll(state, dispatch)
      break
    case 'delete':
      deleteSelection(state, dispatch)
      break
    case 'cut':
    case 'copy':
      // The browser's own copy and cut fire the events ProseMirror serializes
      // the selection from, as Ctrl+C does. A menu click is the user gesture
      // they need; the focus goes back first, as the menu took it.
      view.focus()
      document.execCommand(action)
      break
    case 'paste':
    case 'pastePlain':
      void paste(action === 'pastePlain', view.dom as HTMLElement)
      break
  }
}

function inSource(action: EditAction, view: CmView): void {
  view.focus()
  switch (action) {
    case 'undo':
      cmUndo(view)
      break
    case 'redo':
      cmRedo(view)
      break
    case 'selectAll':
      cmSelectAll(view)
      break
    case 'delete':
      view.dispatch(view.state.replaceSelection(''))
      break
    case 'cut':
    case 'copy':
      document.execCommand(action)
      break
    case 'paste':
    case 'pastePlain':
      void paste(true, view.contentDOM)
      break
  }
}

export function handleEditAction(action: EditAction): void {
  const field = focusedField()
  if (field) {
    if (action === 'paste' || action === 'pastePlain') void pasteIntoField(field)
    else document.execCommand(action === 'delete' ? 'delete' : action)
    return
  }
  const source = sourceView()
  if (source) {
    inSource(action, source)
    return
  }
  withView((view) => inProse(action, view))
}
